import React, { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import AddressPicker from '../Address/AddressPicker';
import Button from '../Button/Button';
import Modal, { ModalBody, ModalFooter } from '../Modal';
import { Input, Label } from '../Form';
import useNotify from '../../hooks/useNotify';
import { ApolloError } from '@apollo/client/errors';
import { ucFirst } from '@ecosconnect/core/src/helpers/string';
import UserPicker from '../User/UserPicker';
import Form from '../Form/Form';
import FormColumn from '../Form/FormColumn';
import FormRow from '../Form/FormRow';
import CustomerPicker from '../Customer/CustomerPicker';
import { WaterSiteType, WaterSiteTypePicker } from './WaterSiteTypePicker';
import { gql } from '@/modes/waterSupplier/gql';

export const waterSiteFields = gql(`
  fragment WaterSiteFields on WaterSite {
    id
    addressId
    name
    siteNumber
    type
    lastSurveyDate
    nextSurveyDate
    surveyIntervalYears
    lastInspectionDate
    nextInspectionDate
    owner {
      id
      name
      firstName
      lastName
      emails {
        subType
        value
      }
      phones {
        subType
        value
      }
    }
    contact {
      id
      name
      firstName
      lastName
      emails {
        subType
        value
      }
      phones {
        subType
        value
      }
    }
    supervisor {
      id
      name
      firstName
      lastName
      emails {
        subType
        value
      }
      phones {
        subType
        value
      }
    }
    assignedToProfileId
    assignedTo {
      id
      name
      firstName
      lastName
    }
    recycledWaterServices {
      id
      siteNumber
      meterNumber
      pin
      type
      status
    }
    address(required: true) {
      street1
      street2
      city
      state
      zip
      zipExt
    }
  }
`);

export type WaterSiteInput = {
  addressId: string;
  name: string;
  siteNumber?: string;
  type: WaterSiteType;
  lastSurveyed?: string;
  nextSurveyDate?: string;
  surveyIntervalYears: number;
  ownerProfileId?: string;
  contactProfileId?: string;
  supervisorProfileId?: string;
  assignedToProfileId?: string;
};

type WaterSiteEditModalProps = {
  onClose: () => void;
  waterSite?: Partial<WaterSiteInput>;
  waterSiteId?: string;
  readOnly?: boolean;
} & (
  | {
      waterSiteId: string;
      action: 'edit';
    }
  | {
      action: 'add';
    }
);

const CREATE_WATER_SITE = gql(
  `
  mutation CreateWaterSite($input: WaterSiteInput!) {
    createWaterSite(input: $input) {
      ...WaterSiteFields
    }
  }
`,
  [waterSiteFields]
);

const UPDATE_WATER_SITE = gql(
  `
  mutation UpdateWaterSite($waterSiteId: ID!, $input: WaterSiteInput!) {
    updateWaterSite(waterSiteId: $waterSiteId, input: $input) {
      ...WaterSiteFields
    }
  }
`,
  [waterSiteFields]
);

const WaterSiteEditModal: React.FC<WaterSiteEditModalProps> = ({
  onClose,
  waterSite,
  waterSiteId,
  action,
  readOnly = false,
}) => {
  const [waterSiteInput, setWaterSiteInput] = useState<WaterSiteInput>({
    addressId: waterSite?.addressId ?? '',
    name: waterSite?.name ?? '',
    siteNumber: waterSite?.siteNumber ?? '',
    type: waterSite?.type ?? 'HAZARD',
    lastSurveyed: waterSite?.lastSurveyed,
    nextSurveyDate: waterSite?.nextSurveyDate,
    surveyIntervalYears: waterSite?.surveyIntervalYears ?? 1,
    ownerProfileId: waterSite?.ownerProfileId,
    contactProfileId: waterSite?.contactProfileId,
    supervisorProfileId: waterSite?.supervisorProfileId,
    assignedToProfileId: waterSite?.assignedToProfileId,
  });

  const { showError, showSuccess } = useNotify();

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    updateField(name, value);
  };

  const updateField = <F extends keyof WaterSiteInput>(
    name: F,
    value?: WaterSiteInput[F]
  ) => {
    setWaterSiteInput((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const onCompleted = () => {
    showSuccess('Water site saved');
    onClose();
  };

  const onError = (error: ApolloError) => {
    showError('Error saving water site', error);
  };

  const [
    saveWaterSite,
    { data: saveData, loading: saveLoading, error: saveError },
  ] =
    action === 'edit'
      ? useMutation(UPDATE_WATER_SITE, {
          onCompleted,
          onError,
        })
      : useMutation(CREATE_WATER_SITE, {
          onCompleted,
          onError,
        });

  const save = useCallback(async () => {
    if (!waterSiteInput.name) {
      showError('Please enter a name');
      return;
    }
    if (!waterSiteInput.type) {
      showError('Please select a type');
      return;
    }
    if (!waterSiteInput.addressId) {
      showError('Please enter an address');
      return;
    }
    await saveWaterSite({
      variables: { waterSiteId: waterSiteId!, input: waterSiteInput },
    });
  }, [waterSiteId, waterSiteInput]);

  return (
    <Modal onClose={onClose} title={`${ucFirst(action)} Water Site`}>
      <ModalBody>
        <Form>
          <FormColumn>
            <Label>Name</Label>
            <Input
              width="100%"
              type="text"
              value={waterSiteInput.name}
              onChange={handleChange}
              name="name"
              placeholder="Name"
              autoComplete="off"
              disabled={readOnly}
            />
          </FormColumn>
          <FormRow>
            <FormColumn>
              <Label>Site Number</Label>
              <Input
                width="100%"
                type="text"
                value={waterSiteInput.siteNumber}
                onChange={handleChange}
                name="siteNumber"
                placeholder="Site Number"
                autoComplete="off"
                disabled={readOnly}
              />
            </FormColumn>
            <FormColumn>
              <Label>Site Type</Label>
              <WaterSiteTypePicker
                isMulti={false}
                value={waterSiteInput.type}
                isDisabled={readOnly}
                onChange={(value) => {
                  updateField('type', value);
                }}
              />
            </FormColumn>
          </FormRow>
          <FormColumn>
            <Label>Address</Label>
            <AddressPicker
              addressId={waterSiteInput.addressId}
              onChange={(newAddress) =>
                updateField('addressId', newAddress?.id)
              }
              allowCreate
              isDisabled={readOnly}
            />
          </FormColumn>
          <FormColumn>
            <Label>Assigned To</Label>
            <UserPicker
              autoFocus
              companies={false}
              isClearable
              isDisabled={readOnly}
              onChange={(user) => {
                setWaterSiteInput((prevState) => ({
                  ...prevState,
                  assignedToProfileId: user?.profileId,
                }));
              }}
              userId={waterSiteInput.assignedToProfileId}
            />
          </FormColumn>
          <FormRow>
            <FormColumn>
              <Label>Owner</Label>
              <CustomerPicker
                customerProfileId={waterSiteInput.ownerProfileId}
                serviceAddressId={waterSiteInput.addressId}
                serviceAddressWhere={
                  waterSiteInput.addressId
                    ? {
                        active: true,
                        addressId: waterSiteInput.addressId,
                      }
                    : undefined
                }
                isClearable
                isDisabled={readOnly}
                onChange={(customer) =>
                  setWaterSiteInput((current) => {
                    return {
                      ...current,
                      ownerProfileId: customer?.profileId,
                    };
                  })
                }
              />
            </FormColumn>
            <FormColumn>
              <Label>Site Contact</Label>
              <CustomerPicker
                customerProfileId={waterSiteInput.contactProfileId}
                serviceAddressId={waterSiteInput.addressId}
                serviceAddressWhere={
                  waterSiteInput.addressId
                    ? {
                        active: true,
                        addressId: waterSiteInput.addressId,
                      }
                    : undefined
                }
                isClearable
                isDisabled={readOnly}
                onChange={(customer) =>
                  setWaterSiteInput((current) => {
                    return {
                      ...current,
                      contactProfileId: customer?.profileId,
                    };
                  })
                }
              />
            </FormColumn>
          </FormRow>
        </Form>
      </ModalBody>
      <ModalFooter>
        {!readOnly && (
          <Button icon="save" disabled={saveLoading} onClick={save}>
            Save
          </Button>
        )}
        <Button variant="outline" icon="slash-circle" onClick={onClose}>
          {readOnly ? 'Close' : 'Cancel'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default WaterSiteEditModal;
